/**
 * Analytics Utility Functions for Freedom Contracting Group
 * 
 * This module provides tracking functions for user interactions.
 * Events are pushed to GTM dataLayer, which handles consent and GA4.
 * 
 * FLOW:
 * 1. Events are pushed to window.dataLayer
 * 2. GTM picks up these events via triggers
 * 3. GTM consent mode determines if GA4 tags fire
 * 4. If user has consented, GA4 records the event
 * 
 * USAGE: Import specific functions where needed:
 * import { trackFormSubmission, trackPhoneClick } from './analytics';
 */

import { hasAnalyticsConsent, GA_MEASUREMENT_ID } from './CookieConsent';

/**
 * Ensure dataLayer exists
 */
const ensureDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
};

/**
 * Check if gtag is available (loaded by GTM)
 */
const isGtagAvailable = () => {
  return typeof window.gtag === 'function';
};

/**
 * Push to dataLayer - this is the primary method for GTM integration
 * Events pushed here will be picked up by GTM triggers
 */
const pushToDataLayer = (data) => {
  ensureDataLayer();
  window.dataLayer.push(data);
  return true;
};

/**
 * Track a custom event
 * @param {string} eventName - Name of the event (e.g., 'form_submit', 'button_click')
 * @param {object} eventParams - Additional parameters for the event
 * 
 * Example usage:
 * trackEvent('contact_form_submit', { form_type: 'quote_request' });
 */
export const trackEvent = (eventName, eventParams = {}) => {
  // Always push to dataLayer for GTM
  pushToDataLayer({
    event: eventName,
    ...eventParams
  });
  
  // Also send to GA directly if gtag is available and consent given
  if (isGtagAvailable() && hasAnalyticsConsent()) {
    window.gtag('event', eventName, eventParams);
  }
  
  if (process.env.NODE_ENV === 'development') {
    console.log(`📊 Analytics Event: ${eventName}`, eventParams);
  }
  
  return true;
};

/**
 * Track a page view (useful for SPA navigation)
 * @param {string} pagePath - The path of the page (e.g., '/services')
 * @param {string} pageTitle - The title of the page
 * 
 * Example usage:
 * trackPageView('/team', 'Our Team - Freedom Contracting Group');
 */
export const trackPageView = (pagePath, pageTitle) => {
  pushToDataLayer({
    event: 'page_view',
    page_path: pagePath,
    page_title: pageTitle
  });
  
  if (isGtagAvailable() && hasAnalyticsConsent()) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: pagePath,
      page_title: pageTitle
    });
  }
  
  if (process.env.NODE_ENV === 'development') {
    console.log(`📊 Analytics Page View: ${pagePath}`);
  }
  
  return true;
};

/**
 * Track modal/overlay opens (for SPA virtual page views)
 * @param {string} modalName - Name of the modal (e.g., 'team', 'contact')
 */
export const trackModalOpen = (modalName) => {
  const pagePath = `/${modalName}`;
  const pageTitle = `${modalName.charAt(0).toUpperCase() + modalName.slice(1)} - Freedom Contracting Group`;
  
  trackEvent('modal_open', {
    modal_name: modalName
  });
  
  trackPageView(pagePath, pageTitle);
};

/**
 * Track modal/overlay closes
 * @param {string} modalName - Name of the modal
 */
export const trackModalClose = (modalName) => {
  trackEvent('modal_close', {
    modal_name: modalName
  });
};

/**
 * Track outbound link clicks
 * @param {string} url - The URL being clicked
 * @param {string} linkText - The text of the link
 * 
 * Example usage:
 * trackOutboundLink('https://facebook.com/freedomcg', 'Facebook');
 */
export const trackOutboundLink = (url, linkText = '') => {
  trackEvent('outbound_click', {
    event_category: 'Outbound Link',
    link_url: url,
    link_text: linkText
  });
};

/**
 * Track form submissions
 * @param {string} formName - Name/identifier of the form
 * @param {boolean} success - Whether the submission was successful
 * 
 * Example usage:
 * trackFormSubmission('contact_form', true);
 */
export const trackFormSubmission = (formName, success = true) => {
  trackEvent('form_submission', {
    event_category: 'Form',
    form_name: formName,
    submission_success: success
  });
  
  // Also track as conversion event
  if (success) {
    trackEvent('generate_lead', {
      event_category: 'Conversion',
      form_name: formName
    });
  }
};

/**
 * Track form start (user begins filling out form)
 * @param {string} formName - Name/identifier of the form
 */
export const trackFormStart = (formName) => {
  trackEvent('form_start', {
    event_category: 'Form',
    form_name: formName
  });
};

/**
 * Track form errors
 * @param {string} formName - Name/identifier of the form
 * @param {string} errorMessage - The error message
 */
export const trackFormError = (formName, errorMessage) => {
  trackEvent('form_error', {
    event_category: 'Form',
    form_name: formName,
    error_message: errorMessage
  });
};

/**
 * Track phone number clicks
 * @param {string} phoneNumber - The phone number clicked
 * @param {string} location - Where on the page the click occurred
 * 
 * Example usage:
 * trackPhoneClick('312-675-6000', 'header');
 */
export const trackPhoneClick = (phoneNumber, location = '') => {
  trackEvent('phone_click', {
    event_category: 'Contact',
    event_label: phoneNumber,
    phone_number: phoneNumber,
    click_location: location
  });
};

/**
 * Track email link clicks
 * @param {string} email - The email address clicked
 * @param {string} location - Where on the page the click occurred
 * 
 * Example usage:
 * trackEmailClick('hello@freedomcg.net', 'footer');
 */
export const trackEmailClick = (email, location = '') => {
  trackEvent('email_click', {
    event_category: 'Contact',
    event_label: email,
    email_address: email,
    click_location: location
  });
};

/**
 * Track CTA button clicks
 * @param {string} buttonName - Name/identifier of the button
 * @param {string} location - Where on the page the button is located
 * 
 * Example usage:
 * trackCTAClick('get_quote', 'hero_section');
 */
export const trackCTAClick = (buttonName, location = '') => {
  trackEvent('cta_click', {
    event_category: 'CTA',
    event_label: buttonName,
    button_name: buttonName,
    button_location: location
  });
};

/**
 * Track scroll depth milestones
 * @param {number} percentage - The scroll depth percentage (25, 50, 75, 100)
 * 
 * Example usage:
 * trackScrollDepth(50);
 */
export const trackScrollDepth = (percentage) => {
  trackEvent('scroll_depth', {
    event_category: 'Engagement',
    event_label: `${percentage}%`,
    depth_percentage: percentage,
    non_interaction: true
  });
};

/**
 * Track time on page
 * @param {number} seconds - Time spent on page in seconds
 * 
 * Example usage:
 * trackTimeOnPage(60);
 */
export const trackTimeOnPage = (seconds) => {
  trackEvent('time_on_page', {
    event_category: 'Engagement',
    event_label: `${seconds}s`,
    time_seconds: seconds,
    non_interaction: true
  });
};

/**
 * Track social media link clicks
 * @param {string} platform - The social platform (facebook, linkedin, instagram)
 * @param {string} url - The URL clicked
 */
export const trackSocialClick = (platform, url) => {
  trackEvent('social_click', {
    event_category: 'Social',
    event_label: platform,
    social_platform: platform,
    link_url: url
  });
  
  // Also track as outbound
  trackOutboundLink(url, platform);
};

/**
 * Track navigation clicks
 * @param {string} menuItem - The menu item clicked
 * @param {string} location - 'desktop' or 'mobile'
 */
export const trackNavigation = (menuItem, location = 'desktop') => {
  trackEvent('navigation_click', {
    event_category: 'Navigation',
    event_label: menuItem,
    menu_item: menuItem,
    nav_location: location
  });
};

/**
 * Track file downloads
 * @param {string} fileName - Name of the file
 * @param {string} fileType - Type of file (pdf, docx, etc.)
 */
export const trackDownload = (fileName, fileType) => {
  trackEvent('file_download', {
    event_category: 'Download',
    event_label: fileName,
    file_name: fileName,
    file_type: fileType
  });
};

/**
 * Track video engagement
 * @param {string} videoTitle - Title of the video
 * @param {string} action - 'play', 'pause', 'complete', 'progress'
 * @param {number} progress - Percentage watched (optional)
 */
export const trackVideo = (videoTitle, action, progress = null) => {
  const params = {
    event_category: 'Video',
    event_label: videoTitle,
    video_title: videoTitle,
    video_action: action
  };
  
  if (progress !== null) {
    params.video_progress = progress;
  }
  
  trackEvent('video_engagement', params);
};

/**
 * Initialize scroll depth tracking
 * Call this once on page load to automatically track scroll milestones
 */
export const initScrollTracking = () => {
  const milestones = [25, 50, 75, 100];
  const tracked = new Set();
  
  const handleScroll = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = Math.round((scrollTop / docHeight) * 100);
    
    milestones.forEach(milestone => {
      if (scrollPercent >= milestone && !tracked.has(milestone)) {
        tracked.add(milestone);
        trackScrollDepth(milestone);
      }
    });
  };
  
  // Throttle scroll handler
  let ticking = false;
  window.addEventListener('scroll', () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        handleScroll();
        ticking = false;
      });
      ticking = true;
    }
  });
};

/**
 * Initialize time on page tracking
 * Tracks time spent at 30s, 60s, 120s, 300s intervals
 */
export const initTimeTracking = () => {
  const intervals = [30, 60, 120, 300];
  
  intervals.forEach(seconds => {
    setTimeout(() => {
      trackTimeOnPage(seconds);
    }, seconds * 1000);
  });
};

const analytics = {
  trackEvent,
  trackPageView,
  trackModalOpen,
  trackModalClose,
  trackOutboundLink,
  trackFormSubmission,
  trackFormStart,
  trackFormError,
  trackPhoneClick,
  trackEmailClick,
  trackCTAClick,
  trackScrollDepth,
  trackTimeOnPage,
  trackSocialClick,
  trackNavigation,
  trackDownload,
  trackVideo,
  initScrollTracking,
  initTimeTracking
};

export default analytics;
